import { v4 as uuidv4 } from 'uuid';
import { useServerStorage, EStorageType, EStorageSource } from './useServerStorage';
import { setStatsDetailParams } from '@op/utils/storageUtils';
import { navigate, JumpType } from '@home/utils/utils';
import { setCursorLoading } from '../utils/cursorLoading';

/** URL 中下钻参数的 id key */
export const DRILLDOWN_ID_KEY = 'statsDetailId';

/** 下钻参数存储模式 */
export enum EDrilldownMode {
  /** 服务端缓存（文档标注的目标页面，如 Detail / Stats Detail / VersionAnalysis Details，URL 带 _s 标记） */
  Server = 'server',
  /** 本地缓存（特殊页面，如 Feeds / KeyOpinions / Compare / News，按原 goStatsDetailAction 方式写入 sessionStorage 原始参数，无服务端请求） */
  Local = 'local',
}

type TUseDrilldownParamsOptions = {
  /** 跳转方式 */
  jumpType?: JumpType | string;
  /** 新窗口参数 */
  windowParams?: Record<string, any>;
};

type TUseDrilldownParamsReturn = {
  /** 下钻跳转：保存参数并跳转 */
  goPage: (params: {
    path: string;
    filter: any;
    extra?: string;
    stats?: any;
    /**
     * 存储模式：
     * - Server（默认）：文档标注的目标页面，走服务端缓存，失败 fallback 本地
     * - Local：特殊页面按原 goStatsDetailAction 方式写本地 sessionStorage，不请求服务端
     */
    mode?: EDrilldownMode;
  }) => Promise<void>;
  /** 从当前页面 URL 读取下钻参数（目标页面使用，一站式） */
  readParams: () => Promise<any | null>;
  /** 是否正在保存 */
  saving: boolean;
  /** 是否正在读取 */
  loading: boolean;
};

/**
 * 下钻参数管理 Hook
 *
 * 用法（发起方页面）：
 *   const { goPage, saving } = useDrilldownParams({ jumpType: 'HistoryPush' });
 *   // 默认 Server：文档标注的目标页面，走服务端缓存，失败 fallback 本地
 *   await goPage({ path: '/opinion/Detail', filter });
 *   // 特殊页面（Feeds / KeyOpinions / Compare / News 等）：需显式传 Local，按原缓存方式
 *   await goPage({ path: '/opinion/Feeds/Feeds?tab=socialMedia', filter, mode: EDrilldownMode.Local });
 *
 * 用法（目标页面）：
 *   const { readParams, loading } = useDrilldownParams();
 *   const params = await readParams();
 */
export function useDrilldownParams(options: TUseDrilldownParamsOptions = {}): TUseDrilldownParamsReturn {
  const { jumpType = 'HistoryPush', windowParams } = options;
  const { save, getFromUrl, saving, loading } = useServerStorage({
    type: EStorageType.Drilldown,
    fallback: true,
  });

  const goPage = async ({
    path,
    filter,
    extra = '',
    stats = {},
    mode = EDrilldownMode.Server,
  }: {
    path: string;
    filter: any;
    extra?: string;
    stats?: any;
    mode?: EDrilldownMode;
  }) => {
    // 保存期间全局鼠标 loading，结束后恢复
    setCursorLoading(true);
    try {
      // 特殊页面：按原 goStatsDetailAction 方式写入 sessionStorage 原始参数（{...filter, goTime}），
      // 目标页 useDefaultFilter 直接通过 getStatsDetailParams() 读取，不产生服务端请求
      if (mode === EDrilldownMode.Local) {
        const id = uuidv4();
        setStatsDetailParams(filter, id);

        const split = path.includes('?') ? '&' : '?';
        const resolvedUrl = `${path}${split}${DRILLDOWN_ID_KEY}=${id}&_s=${EStorageSource.Local}${extra}`;
        navigate(resolvedUrl, { jumpType, windowParams, historyState: stats });
        return;
      }

      // 文档标注的目标页面：服务端缓存，失败 fallback 本地
      const data = { filter, path, extra, stats };
      const result = await save(data);
      if (!result) return;

      // URL 带上 _s 标记数据来源，读取方据此优先从对应来源读取
      const split = path.includes('?') ? '&' : '?';
      const resolvedUrl = `${path}${split}${DRILLDOWN_ID_KEY}=${result.id}&_s=${result.source}${extra}`;
      navigate(resolvedUrl, { jumpType, windowParams, historyState: stats });
    } finally {
      setCursorLoading(false);
    }
  };

  const readParams = async () => {
    return getFromUrl(DRILLDOWN_ID_KEY);
  };

  return { goPage, readParams, saving, loading };
}
